import { Outlet, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { BottomNav } from "./BottomNav";
import { useAuth } from "../hooks/useAuth";

const titles: Record<string, string> = {
  "/": "Dashboard",
  "/eas": "EA List",
  "/calendar": "Profit Calendar",
  "/charts": "Charts",
  "/market": "Market",
  "/news": "Economic News",
  "/settings": "Settings",
  "/profile": "Profile",
};

function pageTitle(pathname: string): string {
  if (titles[pathname]) return titles[pathname];
  if (pathname.startsWith("/eas/")) return "EA Details";
  return "MT5 Dashboard";
}

export function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  return (
    // viewport-fit=cover (index.html) lets the page run under the iPhone notch and home
    // indicator, so iOS reports their real size; these pads keep content out from under the
    // notch, and BottomNav pads itself above the home indicator.
    <div className="flex h-dvh bg-slate-950 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)]">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-800 px-4 sm:px-6">
          <h1 className="text-lg font-semibold text-slate-100">{pageTitle(location.pathname)}</h1>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 border-l border-slate-800 pl-3 sm:flex">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-200">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-rose-400"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>
        {/* Below md the fixed bottom nav covers the last 4rem, so the bottom padding clears it. */}
        <main className="flex-1 overflow-y-auto p-4 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:p-6 sm:pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-6">
          <Outlet />
        </main>
      </div>
      <BottomNav />
    </div>
  );
}
